import { useRef } from "react";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { SelectInput } from "../components/ui/field.js";
import { KIND_OPTIONS, useImports } from "../lib/useImports.js";
import type { ImportBatchDto } from "@grizzly/shared";
import { formatDateTime } from "../lib/format.js";
import { useI18n } from "../i18n/index.js";

export function ImportsPage() {
  const { t } = useI18n();
  const {
    kind,
    setKind,
    fileName,
    setFileName,
    csv,
    setCsv,
    preview,
    batches,
    busy,
    runPreview,
    runCommit,
  } = useImports();
  const fileInput = useRef<HTMLInputElement>(null);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  const batchColumns: Column<ImportBatchDto>[] = [
    { key: "kind", header: t("imports.column.kind"), render: (r) => r.kind },
    { key: "file", header: t("imports.column.file"), render: (r) => r.fileName },
    {
      key: "rows",
      header: t("imports.column.rows"),
      render: (r) => <span className="tabular">{r.inserted}</span>,
    },
    {
      key: "by",
      header: t("imports.column.importedBy"),
      render: (r) => r.actorName ?? "-",
    },
    { key: "at", header: t("common.when"), render: (r) => formatDateTime(r.createdAt) },
  ];

  const issueColumns: Column<{ id: string; row: number; message: string }>[] = [
    {
      key: "row",
      header: t("imports.column.row"),
      render: (r) => <span className="tabular">{r.row}</span>,
    },
    { key: "message", header: t("imports.column.message"), render: (r) => r.message },
  ];

  return (
    <>
      <PageHeader title={t("nav.imports")} description={t("imports.description")} />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectInput
            label={t("imports.importKind")}
            options={KIND_OPTIONS.map((k) => ({ value: k.value, label: t(k.labelKey) }))}
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="import-file" className="text-sm font-medium text-ink">
              {t("imports.csvFile")}
            </label>
            <input
              ref={fileInput}
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="text-sm text-ink-muted"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button variant="secondary" onClick={runPreview} disabled={busy || !csv}>
            {t("imports.dryRun")}
          </Button>
          <Button
            variant="primary"
            onClick={runCommit}
            disabled={busy || !csv || (preview?.issues.length ?? 0) > 0}
          >
            {t("imports.commit")}
          </Button>
          {fileName ? <span className="text-xs text-ink-muted">{fileName}</span> : null}
        </div>
      </Card>

      {preview ? (
        <Card className="mb-4">
          <h2 className="mb-2 text-sm font-semibold text-ink">
            {t("imports.preview", { total: preview.totalRows, ok: preview.okRows })}
          </h2>
          <DataTable
            caption={t("imports.errorsCaption")}
            rows={preview.issues.map((issue, i) => ({ id: String(i), ...issue }))}
            rowKey={(r) => r.id}
            columns={issueColumns}
            emptyTitle={t("imports.noErrors")}
            emptyDescription={t("imports.noErrorsDesc")}
          />
        </Card>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink">{t("imports.recent")}</h2>
        <DataTable
          caption={t("imports.batchesCaption")}
          rows={batches}
          rowKey={(r) => r.id}
          columns={batchColumns}
          emptyTitle={t("imports.noImports")}
          emptyDescription={t("imports.noImportsDesc")}
        />
      </section>
    </>
  );
}
